import React from 'react';

/**
 * The agreement itself, in a readable column.
 *
 * Rendered into an iframe rather than dangerouslySetInnerHTML: the content is
 * authored by the shop, and an iframe keeps its markup and any styling it
 * carries out of the app's own document. The height is driven from the
 * iframe's own content so the document scrolls inside the frame the layout
 * gives it rather than the frame guessing.
 */
export const TermsDocument: React.FC<{
  content?: string | null;
  loading?: boolean;
  className?: string;
  /**
   * How many of the document's sections have been scrolled past, out of how
   * many there are. Reported from inside the frame because that is where the
   * scrolling happens; the caller decides whether to say anything about it.
   */
  onProgress?: (_progress: { read: number; total: number }) => void;
}> = ({ content, loading, className, onProgress }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  // Held in a ref so the scroll listener never has to be torn down and
  // rebuilt when the callback identity changes.
  const progressRef = React.useRef(onProgress);
  progressRef.current = onProgress;

  React.useEffect(() => {
    if (!iframeRef.current || !content) return;
    const iframeDoc =
      iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
    if (!iframeDoc) return;

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
      <head>
          <title>Terms and Conditions</title>
          <style>
              html, body { height: 100%; }
              body {
                margin: 0;
                padding: 1.25rem 1.5rem;
                font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
                font-size: 14px;
                line-height: 1.65;
                color: #3f3f46;
              }
              h1, h2, h3 { color: #18181b; font-size: 15px; margin: 1.5em 0 .4em; }
              h1:first-child, h2:first-child, h3:first-child { margin-top: 0; }
              a { color: inherit; }
          </style>
      </head>
      <body>${content}</body>
      </html>
    `);
    iframeDoc.close();

    // The document is same-origin — this component wrote it — so its headings
    // can be counted and its scrolling watched from here.
    const headings = Array.from(iframeDoc.querySelectorAll('h1, h2, h3'));
    if (!headings.length) return undefined;

    const report = () => {
      const viewport = iframeDoc.documentElement.clientHeight;
      const scrolled = iframeDoc.documentElement.scrollTop || iframeDoc.body.scrollTop;
      // A section counts as read once its heading has left the top of the
      // viewport — the point at which its text has been in front of you.
      const read = headings.filter((heading) => {
        const top = (heading as HTMLElement).offsetTop;
        return top <= scrolled + viewport * 0.5;
      }).length;
      progressRef.current?.({ read, total: headings.length });
    };

    report();
    const win = iframeRef.current.contentWindow;
    win?.addEventListener('scroll', report, { passive: true });
    return () => win?.removeEventListener('scroll', report);
  }, [content]);

  return (
    <div className={className}>
      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading user agreement…</div>
      ) : (
        <iframe
          ref={iframeRef}
          title="Terms and Conditions"
          className="h-full w-full border-none"
          data-testid="terms-document"
        />
      )}
    </div>
  );
};
